import type {
  CheckpointRevertResolution,
  CheckpointRevertResult,
  CheckpointRevertTurn,
  NativeGitReview,
  WorkspaceEntry,
  WorkspacePort,
} from '@crowdex/workspace-protocol';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { createCloudWorkspaceClient } from '@/lib/cloud-runtime/cloud-workspace-client';
import { localWorkspaceServer } from '@/lib/local-runtime';
import { createLocalWorkspaceClient } from '@/lib/workspaces/local-workspace-client';

/** Streamed pty callbacks, shared by both transports. */
export type TerminalHandlers = {
  onOutput(data: string): void;
  onExit(code: number | null): void;
};

/**
 * The single workspace-operation surface the renderer speaks regardless of
 * backend. Local Desktop and Cloud both use the CLI WebSocket frame protocol.
 * `ready` lets call sites await the connection handshake uniformly.
 */
export type WorkspaceClient = {
  readonly ready: Promise<void>;
  listDirectory(relativePath: string): Promise<WorkspaceEntry[]>;
  /** The TCP ports the workspace's processes listen on (a dev server), to forward them. */
  listPorts(): Promise<WorkspacePort[]>;
  readBinaryFile(path: string): Promise<string>;
  readTextFile(path: string): Promise<string>;
  writeBinaryFile(path: string, content: string): Promise<void>;
  writeTextFile(path: string, content: string): Promise<void>;
  copyFile(source: string, target: string): Promise<void>;
  removeFile(path: string): Promise<void>;
  gitReview(): Promise<NativeGitReview>;
  /**
   * The diff between two checkpoints of a turn (a message's `metadata.checkpoint`), without the
   * files another conversation wrote (`exclude`).
   */
  checkpointDiff(
    before: string,
    after: string,
    path?: string,
    exclude?: readonly string[],
  ): Promise<string>;
  /**
   * Puts back the files `turns` own (undoing one turn, or the turns an edited message drops),
   * refused, writing nothing, while a conflict the resolution does not decide remains.
   */
  revertCheckpoints(
    turns: readonly CheckpointRevertTurn[],
    options?: CheckpointRevertResolution & { dryRun?: boolean },
  ): Promise<CheckpointRevertResult>;
  updateGitFile(path: string, action: 'stage' | 'unstage' | 'revert'): Promise<void>;
  spawnTerminal(
    terminalId: string,
    cols: number,
    rows: number,
    handlers: TerminalHandlers,
    env?: Record<string, string>,
  ): Promise<void>;
  writeTerminal(terminalId: string, data: string): void;
  resizeTerminal(terminalId: string, cols: number, rows: number): void;
  killTerminal(terminalId: string): void;
  close(): void;
};

/**
 * Resolves the workspace client for the active backend:
 * - the cloud frame client when a {@link CloudRuntime} is present;
 * - otherwise the same frame client pointed at the local server's workspace server.
 */
export function resolveWorkspaceClient(
  cloudRuntime: CloudRuntime | null,
  workspaceRoot: string,
): WorkspaceClient {
  if (cloudRuntime !== null) {
    return createCloudWorkspaceClient(cloudRuntime);
  }
  const localServer = localWorkspaceServer();
  if (localServer === null) throw new Error('The local workspace server is not ready.');
  return createLocalWorkspaceClient(localServer, workspaceRoot);
}
